import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

export type UrgencyLevel = "low" | "medium" | "high" | "critical";

/// Display names for the urgency scale. They live here, beside the type
/// they describe, rather than in `features/pulse-dashboards`: this file is
/// in `shared/` and G3 forbids reaching up into a feature, which is exactly
/// why `UrgencyBadge` used to render the raw enum instead. The feature's
/// filter dropdown imports these downward, so the badge and the filter
/// cannot drift apart.
export const URGENCY_LABEL: Record<UrgencyLevel, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
};

const URGENCY_TONES: Record<UrgencyLevel, { fg: string; bg: string; ring: string; dot?: boolean }> =
  {
    low: {
      fg: "text-text-secondary",
      bg: "bg-surface-muted",
      ring: "ring-border-base",
    },
    medium: {
      fg: "text-warning-fg",
      bg: "bg-warning-bg",
      ring: "ring-warning-fg/30",
    },
    high: {
      fg: "text-danger-fg",
      bg: "bg-danger-bg",
      ring: "ring-danger-fg/30",
    },
    critical: {
      // Solid red, the alarm-loud tone. `bg-danger` / `ring-danger` were
      // undefined tokens (only danger-bg / danger-fg exist), so the badge
      // rendered white text on no background: invisible. Use the solid
      // destructive token, which is defined.
      fg: "text-destructive-foreground",
      bg: "bg-destructive",
      ring: "ring-destructive",
      dot: true,
    },
  };

/// Hard-edged uppercase badge, `medium`, `high`, or the alarm-loud
/// `critical` (solid red with leading dot).
export function UrgencyBadge({
  level = "medium",
  className,
}: {
  level?: UrgencyLevel;
  className?: string;
}): ReactNode {
  const tone = URGENCY_TONES[level];
  return (
    <span
      className={cn(
        "inline-flex h-5 items-center gap-1 rounded px-2 font-mono text-3xs font-semibold tracking-wider uppercase ring-1 ring-inset",
        tone.bg,
        tone.fg,
        tone.ring,
        className,
      )}
    >
      {tone.dot ? <span className="size-1 rounded-full bg-white" aria-hidden="true" /> : null}
      {URGENCY_LABEL[level]}
    </span>
  );
}

export type ConfidenceLevel = "low" | "medium" | "high";

export const CONFIDENCE_LABEL: Record<ConfidenceLevel, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
};

/// Three-segment fill showing how strongly the pulse data backs a claim.
///
/// The bar carries the word "Confidence" because it renders directly under
/// an "Urgency" column header; unlabelled, three filled pips reading "HIGH"
/// look like a second urgency reading rather than a different measure
/// entirely. That visible text is also the accessible name: the pips are
/// `aria-hidden` decoration, so a screen reader gets "Confidence Medium"
/// from the text itself with no `aria-label` needed (and a plain `span`
/// has no role that would support one).
export function ConfidenceBar({
  level = "medium",
  className,
}: {
  level?: ConfidenceLevel;
  className?: string;
}): ReactNode {
  const filled = level === "high" ? 3 : level === "medium" ? 2 : 1;
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-2xs text-text-tertiary", className)}>
      <span className="inline-flex gap-0.5" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={cn(
              "h-1 w-3.5 rounded-sm",
              i < filled ? "bg-violet-600" : "bg-surface-muted ring-1 ring-inset ring-border-base",
            )}
          />
        ))}
      </span>
      <span>
        Confidence <span className="font-medium">{CONFIDENCE_LABEL[level]}</span>
      </span>
    </span>
  );
}
